/**
 * CapabilityGuard - Modern route and content protection using capability predicates.
 * 
 * This component replaces the legacy ProtectedRoute and PermissionGuard components.
 * It uses the modern capability-based permission system instead of the legacy permission matrix.
 * 
 * Usage:
 * 
 * For route protection:
 * <CapabilityGuard check={(caps) => canViewTasks(caps)} redirectTo="/unauthorized">
 *   <TasksPage />
 * </CapabilityGuard>
 * 
 * For content gating (no redirect):
 * <CapabilityGuard check={(caps) => canCreateTasks(caps)} fallback={<span>Read-only</span>}>
 *   <CreateTaskButton />
 * </CapabilityGuard>
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Capability } from '../../lib/auth/capabilities';
import { getUserRouteAccess } from '../../lib/auth/routeRegistry';
import { resolveRouteAuthority } from '../../lib/auth/routeAuthority';
import {
  isAuthorityProjectionFresh,
  useAuthorityProjectionState,
} from '../../lib/auth/authorityProjectionStore';
import { Loader } from '../ui/Loader';

export interface CapabilityGuardProps {
  /**
   * Predicate function that receives capabilities array and returns whether access is granted.
   */
  check: (caps: Capability[]) => boolean;

  /**
   * Connected-mode predicate over exact server-owned identifiers. Legacy
   * capability translation and route fallbacks are intentionally unavailable.
   */
  connectedCheck?: (caps: ReadonlySet<string>) => boolean;
  
  /**
   * Content to render if the check passes.
   */
  children: React.ReactNode;
  
  /**
   * Route to redirect to if check fails (enables route protection mode).
   * If not provided, renders fallback or null instead (content gating mode).
   */
  redirectTo?: string;
  
  /**
   * Fallback UI to render when check fails (only used when redirectTo is not set).
   */
  fallback?: React.ReactNode;
  
  /**
   * Show loading state while auth is initializing.
   * @default true
   */
  showLoader?: boolean;
}

/**
 * Modern capability-based guard component for routes and content.
 */
export const CapabilityGuard: React.FC<CapabilityGuardProps> = ({
  check,
  connectedCheck,
  children,
  redirectTo,
  fallback = null,
  showLoader = true,
}) => {
  const { user, loading, isDemoMode } = useAuth();
  const location = useLocation();
  const projectionState = useAuthorityProjectionState();
  
  // Show loader while auth is initializing
  if (loading) {
    if (!showLoader) return null;
    return (
      <div aria-busy="true" className="min-h-[120px] flex items-center justify-center">
        <Loader />
      </div>
    );
  }
  
  let hasAccess = false;
  if (isDemoMode === false) {
    const segments = location.pathname.split('/').filter(Boolean);
    let exactRouteScope = false;
    if (projectionState.status === 'confirmed'
      && projectionState.projection.actor.userId === user?.id
      && isAuthorityProjectionFresh(projectionState.projection, projectionState.validatedAt)) {
      try {
        if (segments[0] === 'workspaces' && segments[1]) {
          const routeWorkspaceId = decodeURIComponent(segments[1]);
          exactRouteScope = projectionState.request.workspaceId === routeWorkspaceId
            && projectionState.projection.scope.workspaceId === routeWorkspaceId;
        } else if (segments[0] === 'companies' && segments[1]) {
          const routeCompanyId = decodeURIComponent(segments[1]);
          exactRouteScope = projectionState.request.representedEntity?.type === 'company'
            && projectionState.request.representedEntity.id === routeCompanyId
            && projectionState.projection.scope.representedEntity?.type === 'company'
            && projectionState.projection.scope.representedEntity.id === routeCompanyId;
        }
      } catch {
        exactRouteScope = false;
      }
    }

    if (exactRouteScope && projectionState.status === 'confirmed') {
      const projected = new Set<string>(projectionState.projection.capabilities);
      try {
        hasAccess = connectedCheck
          ? Boolean(connectedCheck(projected))
          : Boolean(check([...projected] as Capability[]));
      } catch {
        hasAccess = false;
      }
    }
  } else {
    // Demo fixtures keep their existing role-derived capability behavior.
    const authority = resolveRouteAuthority(user);
    const caps = [...authority.capabilities];
    try {
      hasAccess = Boolean(check(caps));
    } catch {
      hasAccess = false;
    }

    if (!hasAccess && redirectTo && user) {
      hasAccess = getUserRouteAccess(user, location.pathname).allowed;
    }
  }
  
  // Access denied
  if (!hasAccess) {
    // Route protection mode: redirect
    if (redirectTo) {
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }
    // Content gating mode: show fallback
    return <>{fallback}</>;
  }
  
  // Access granted
  return <>{children}</>;
};

export default CapabilityGuard;
